// Карта с точкой на базе MapLibre
// Компонент позволяет:
// - ставить и перетаскивать одиночную точку
// Обмен снаружи через onChange:
// - для точки: { lat, lon }
import { useEffect, useState, useRef, useCallback } from "react";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  Map,
  FullscreenControl,
  NavigationControl,
} from "@vis.gl/react-maplibre";
import { Button } from "antd";

import TargetIcon from "@/icons/TargetIcon";

import { useObjectsLayer } from "@/hooks/useObjectsLayer";

import ObjectsLayer from "@/components/maps/ObjectsLayer";

import { MAP_STYLES, DEFAULT_VIEW_STATE, CIRCLE_RADIUS } from "@/constants/map";
import PointOnMap from "@/components/maps/PointOnMap";

const DEFAULT_ZOOM = 15;

/**
 * InputPointOnMap - компонент для выбора одной точки на карте. Поддерживает установку кликом и перетаскиванием. А так же отображает все объекты и их поля на карте.
 *
 * Пропсы:
 * - value: текущие данные { lat, lon }
 * - onChange: (value) => void — колбэк при изменении геометрии
 */
const InputPointOnMap = ({
  field,
  objectUuid,
  value,
  color = "#e66a25",
  onChange,
  style,
  ...props
}) => {
  // Флаг для центрирования карты только при первом входящем значении
  const isInitialLoad = useRef(true);
  // Текущее состояние камеры карты
  const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);
  // Координаты точки: { latitude, longitude }
  const [point, setPoint] = useState(null);

  const isDraggingRef = useRef(false);
  const wasDraggingRef = useRef(false);

  const { allObjects, allFields, updateBbox } = useObjectsLayer(
    objectUuid,
    field?.field_name,
  );

  useEffect(() => {
    // Синхронизация входного value -> внутреннее состояние
    if (!value) {
      setPoint(null);
      isInitialLoad.current = false;
      return;
    }

    const coords = Object.values(value);
    if (coords.length >= 2) {
      setPoint({ latitude: coords[0], longitude: coords[1] });

      // Центрируем карту только при первой загрузке
      if (isInitialLoad.current) {
        setViewState((prev) => ({
          ...prev,
          latitude: coords[0],
          longitude: coords[1],
          zoom: DEFAULT_ZOOM,
        }));
        isInitialLoad.current = false;
      }
    }
  }, [value]);

  // Обработка клика по карте для установки точки
  const handleMapClick = useCallback(
    (e) => {
      if (wasDraggingRef.current || isDraggingRef.current) return; // не ставим точку после drag
      const { lng, lat } = e.lngLat;
      setPoint({ latitude: lat, longitude: lng });
      onChange({ lat, lon: lng });
    },
    [onChange],
  );

  // Drag точки без Marker: захват при mousedown рядом с точкой
  const onMouseDown = useCallback(
    (e) => {
      if (!point) return;
      const map = e.target;
      try {
        const pointPx = map.project([point.longitude, point.latitude]);
        const eventPx = map.project([e.lngLat.lng, e.lngLat.lat]);
        const dist = Math.hypot(pointPx.x - eventPx.x, pointPx.y - eventPx.y);
        if (dist <= Math.max(CIRCLE_RADIUS + 4, 12)) {
          isDraggingRef.current = true;
          wasDraggingRef.current = true;
          map.dragPan.disable();
          try {
            map.getCanvas().style.cursor = "grabbing";
          } catch (_) {}
        }
      } catch (_) {
        // noop
      }
    },
    [point],
  );

  const onMouseMove = useCallback(
    (e) => {
      const map = e.target;
      if (isDraggingRef.current) {
        try {
          map.getCanvas().style.cursor = "grabbing";
        } catch (_) {}
        const { lng, lat } = e.lngLat;
        setPoint({ latitude: lat, longitude: lng });
        onChange({ lat, lon: lng });
        return;
      }

      // динамический курсор: pointer при наведении на точку, иначе grab
      try {
        if (!point) {
          map.getCanvas().style.cursor = "grab";
          return;
        }
        const pointPx = map.project([point.longitude, point.latitude]);
        const eventPx = map.project([e.lngLat.lng, e.lngLat.lat]);
        const dist = Math.hypot(pointPx.x - eventPx.x, pointPx.y - eventPx.y);
        map.getCanvas().style.cursor =
          dist <= Math.max(CIRCLE_RADIUS + 4, 12) ? "pointer" : "grab";
      } catch (_) {}
    },
    [onChange, point],
  );

  const onMouseUp = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const map = e.target;
    isDraggingRef.current = false;
    try {
      map.dragPan.enable();
    } catch (_) {}
    // небольшой таймаут, чтобы игнорировать click после drag
    setTimeout(() => {
      wasDraggingRef.current = false;
    }, 50);
    try {
      map.getCanvas().style.cursor = "grab";
    } catch (_) {}
  }, []);

  // Обработка клика по кнопке центрирования к точке
  const handleTargetClick = () => {
    if (!point) return;
    setViewState((prev) => ({
      ...prev,
      latitude: point.latitude,
      longitude: point.longitude,
      zoom: Math.max(prev.zoom || 0, DEFAULT_ZOOM),
    }));
  };

  return (
    <Map
      {...props}
      {...viewState}
      style={{ width: "100%", height: "500px", ...style }}
      mapStyle={MAP_STYLES.voyager}
      attributionControl={false}
      onMove={(evt) => {
        setViewState(evt.viewState);
      }}
      onMoveEnd={(evt) => {
        setViewState(evt.viewState);
        updateBbox(evt.target);
      }}
      onClick={handleMapClick}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onLoad={(e) => {
        updateBbox(e.target);
        try {
          e.target.getCanvas().style.cursor = "grab";
        } catch (_) {}
      }}
      preserveDrawingBuffer={true}
    >
      <FullscreenControl />
      <NavigationControl showCompass={false} />

      {/* Отображает все поля других объектов */}
      <ObjectsLayer objects={allObjects} color="#bbbbbb" />
      {/* Отображает остальные поля текущего объекта */}
      <ObjectsLayer objects={allFields} color={`${color}80`} />

      {/* Отображает точку текущего объекта */}
      {point && (
        <PointOnMap lon={point.longitude} lat={point.latitude} color={color} />
      )}

      {/* Кнопка центрирования карты к установленному пину */}
      <Button
        icon={<TargetIcon />}
        disabled={!point}
        style={{
          position: "absolute",
          right: 10,
          bottom: 10,
        }}
        onClick={handleTargetClick}
      >
        К точке
      </Button>
    </Map>
  );
};

export default InputPointOnMap;
